// Client-side analytics for 67Cafe. Stores a stable anonymous visitor id in
// localStorage and pings the backend with lightweight events.
const LS_KEY = "cafe-visitor-id-v1";

const uuid = () => {
  if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  return `v-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

export const getVisitorId = () => {
  try {
    let v = localStorage.getItem(LS_KEY);
    if (!v) {
      v = uuid();
      localStorage.setItem(LS_KEY, v);
    }
    return v;
  } catch {
    return "anon";
  }
};

/** Overwrite the persistent visitor_id — used when the auth callback tells us
 *  the server folded our anonymous row into an older canonical linked row. */
export const setVisitorId = (v) => {
  if (!v) return;
  try { localStorage.setItem(LS_KEY, v); } catch { /* private mode */ }
};

const BASE = () => process.env.REACT_APP_BACKEND_URL;

// Roughly bucket the device from the user agent + viewport width.
const detectDevice = () => {
  const ua = (navigator.userAgent || "").toLowerCase();
  const w = window.innerWidth || 0;
  if (/ipad|tablet|kindle|silk|playbook/.test(ua) || (w >= 768 && w < 1024)) return "tablet";
  if (/mobi|iphone|ipod|android.*mobile|phone/.test(ua) || w < 768) return "mobile";
  return "desktop";
};

const collectMeta = () => ({
  screen_w: window.screen?.width || 0,
  screen_h: window.screen?.height || 0,
  viewport_w: window.innerWidth || 0,
  viewport_h: window.innerHeight || 0,
  pixel_ratio: window.devicePixelRatio || 1,
  orientation:
    (window.matchMedia && window.matchMedia("(orientation: portrait)").matches)
      ? "portrait"
      : "landscape",
  language: (navigator.language || "").split("@")[0].slice(0, 5),
  device: detectDevice(),
  tz: (Intl.DateTimeFormat().resolvedOptions().timeZone) || "",
});

// Read ?ref=<visitor_id> from the current URL (or from a stored copy so a
// reload doesn't lose the attribution). Ignore self-references.
export const getReferralVisitorId = () => {
  const own = getVisitorId();
  try {
    const params = new URLSearchParams(window.location.search || "");
    const q = (params.get("ref") || "").trim();
    if (q) {
      if (q !== own) {
        try { localStorage.setItem("kidz-ref", q); } catch { /* private mode */ }
      }
      return q === own ? "" : q;
    }
    const stored = (localStorage.getItem("kidz-ref") || "").trim();
    return stored && stored !== own ? stored : "";
  } catch {
    return "";
  }
};

export const trackVisit = async (name, dob, avatar, { profileUpdate = false } = {}) => {
  try {
    await fetch(`${BASE()}/api/analytics/visit`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        visitor_id: getVisitorId(),
        name: name || "",
        dob: dob || "",
        avatar: avatar || "",
        profile_update: !!profileUpdate,
        referred_by: getReferralVisitorId(),
        referrer: document.referrer || "",
        path: window.location.pathname || "",
        ua: navigator.userAgent || "",
        ...collectMeta(),
      }),
      credentials: "include",
    });
  } catch {
    /* analytics failures never break the app */
  }
};

export const trackEvent = async (kind, extra = {}) => {
  try {
    await fetch(`${BASE()}/api/analytics/event`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        visitor_id: getVisitorId(),
        kind,
        path: window.location.pathname || "",
        ...extra,
      }),
      credentials: "include",
    });
  } catch {
    /* ignore */
  }
};
